import * as React from 'react';
import { Modal } from 'antd';
import copy from 'copy-to-clipboard';
import { toPng } from 'html-to-image';
import { QRCodeSVG } from 'qrcode.react';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import fMessage from '../fMessage';
import FTooltip from '../FTooltip';
import FIcons from '../FIcons';
import styles from './index.less';

type WebpackImg = { default?: string };

function resolveImgSrc(src: string | WebpackImg | undefined): string {
  if (!src) {
    return '';
  }
  if (typeof src === 'string') {
    return src;
  }
  return src.default || '';
}

interface FNodeShareProps {
  children: React.ReactNode;
  /** 节点名称（分享文案、微博标题等） */
  title: string;
  /** 预览卡片主标题，默认与 title 相同 */
  cardTitle?: string;
  /** 预览卡片简介 */
  introduction?: string;
  url: string;
  cover?: string;
  onClickShare?(): void;
  /** 默认封面（支持 `import * as img from 'x.png'`） */
  defaultCover?: string | WebpackImg;
}

type ShareItem = { id: string; name: string; icon: string; bgColor: string };

const SHARE_ITEMS: ShareItem[] = [
  { id: 'qqZone', name: 'QQ空间', icon: 'fl-icon-kongjian', bgColor: '#66d8f7' },
  { id: 'qq', name: 'QQ好友', icon: 'fl-icon-QQ', bgColor: '#5382d3' },
  { id: 'weibo', name: '新浪微博', icon: 'fl-icon-weibo', bgColor: '#ff6f68' },
  { id: 'wechat', name: '微信好友', icon: 'fl-icon-weixin', bgColor: '#1ec76f' },
  { id: 'douban', name: '豆瓣', icon: 'fl-icon-douban', bgColor: '#42a151' },
];

function nodeDomainFromNodeUrl(nodeUrl: string): string {
  try {
    const host = new URL(nodeUrl).hostname;
    const first = host.split('.')[0];
    return first && first !== 'www' ? first : '';
  } catch {
    return '';
  }
}

interface HandleDoubanUrlParams {
  href: string;
  name: string;
  text: string;
  image: string;
}

function handleDoubanUrl(params: HandleDoubanUrlParams): string {
  const search: string = Object.entries(params)
    .map((p) => p[0] + '=' + encodeURIComponent(p[1]))
    .join('&');
  return `https://www.douban.com/share/service?${search}`;
}

/** 最多 maxLines 行；超出则截断并加英文三点（与产品「三个点」一致，避免 line-clamp 裁切 U+2026 只露出两点） */
function truncateTextToMaxLines(containerEl: HTMLElement, fullText: string, maxLines: number): string {
  if (!fullText || fullText === '\u00a0') {
    return fullText;
  }
  const cs = getComputedStyle(containerEl);
  const widthPx = containerEl.clientWidth;
  if (widthPx <= 0) {
    return fullText;
  }
  const lineHeightPx = parseFloat(cs.lineHeight);
  const fontSizePx = parseFloat(cs.fontSize);
  const lineHeight =
    Number.isFinite(lineHeightPx) && lineHeightPx > 0 ? lineHeightPx : fontSizePx * 1.5;
  const maxH = lineHeight * maxLines;

  const m = document.createElement('div');
  m.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    'visibility:hidden',
    'pointer-events:none',
    `width:${widthPx}px`,
    `font-size:${cs.fontSize}`,
    `font-family:${cs.fontFamily}`,
    `font-weight:${cs.fontWeight}`,
    `font-style:${cs.fontStyle}`,
    `line-height:${cs.lineHeight}`,
    `letter-spacing:${cs.letterSpacing}`,
    'white-space:normal',
    `word-break:${cs.wordBreak}`,
    `overflow-wrap:${cs.overflowWrap}`,
    `text-align:${cs.textAlign}`,
  ].join(';');
  document.body.appendChild(m);

  m.textContent = fullText;
  if (m.scrollHeight <= maxH + 1) {
    document.body.removeChild(m);
    return fullText;
  }

  const ell = '...';
  let low = 0;
  let high = fullText.length;
  let best = ell;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = fullText.slice(0, mid) + ell;
    m.textContent = candidate;
    if (m.scrollHeight <= maxH + 1) {
      best = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  document.body.removeChild(m);
  return best;
}

export function FNodeShare({
  children,
  title,
  cardTitle,
  introduction = '',
  url,
  cover,
  onClickShare,
  defaultCover,
}: FNodeShareProps) {
  const headline = cardTitle ?? title;
  const shareText = `我在freelog发现一个不错的节点：${title} ${url}`;
  const nodeDomain = nodeDomainFromNodeUrl(url);

  const [popupShow, setPopupShow] = React.useState(false);
  const [qrcodeShow, setQrcodeShow] = React.useState(false);
  const [qrcodeInfo, setQrcodeInfo] = React.useState<{ name: string; url: string }>({
    name: '',
    url: '',
  });
  const [creatorName, setCreatorName] = React.useState('');
  const [creatorAvatar, setCreatorAvatar] = React.useState('');

  const previewCardRef = React.useRef<HTMLDivElement>(null);
  const cardDescRef = React.useRef<HTMLDivElement>(null);

  const [cardDescShown, setCardDescShown] = React.useState(() =>
    introduction ? introduction : '\u00a0',
  );

  React.useLayoutEffect(() => {
    if (!popupShow) {
      return;
    }
    const src = introduction ? introduction : '\u00a0';
    let cancelled = false;

    function commit() {
      if (cancelled) {
        return;
      }
      const node = cardDescRef.current;
      if (!node || node.clientWidth <= 0) {
        setCardDescShown(src);
        return;
      }
      setCardDescShown(truncateTextToMaxLines(node, src, 2));
    }

    commit();

    const el = cardDescRef.current;
    if (!el || el.clientWidth <= 0) {
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(commit);
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [popupShow, introduction]);

  function closeAll() {
    setPopupShow(false);
    setQrcodeShow(false);
  }

  function closeQr() {
    setQrcodeShow(false);
  }

  React.useEffect(() => {
    if (!popupShow) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, ret, errCode } = await FServiceAPI.User.currentUserInfo();
        if (cancelled || ret !== 0 || errCode !== 0 || !data) {
          return;
        }
        const name =
          (data as { username?: string; userDetail?: { nickname?: string } }).username ||
          (data as { userDetail?: { nickname?: string } }).userDetail?.nickname ||
          '';
        const uid = (data as { userId?: number }).userId ?? FUtil.Tool.getUserIDByCookies();
        setCreatorName(name);
        setCreatorAvatar(FUtil.Tool.getAvatarUrl(uid));
      } catch {
        /* 展示层兜底，不阻断弹窗 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [popupShow]);

  React.useEffect(() => {
    if (!popupShow && !qrcodeShow) {
      setCreatorName('');
      setCreatorAvatar('');
    }
  }, [popupShow, qrcodeShow]);

  function runShare(item: Pick<ShareItem, 'id' | 'name'>) {
    onClickShare?.();
    const summary = '';

    if (item.id === 'qqZone') {
      const shareWeb = `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodeURIComponent(
        url,
      )}&desc=${encodeURIComponent(shareText)}&summary=${encodeURIComponent(
        summary,
      )}&title=${encodeURIComponent(title)}&pics=${encodeURIComponent(cover || '')}`;
      window.open(shareWeb);
    } else if (item.id === 'weibo') {
      const weiboTitle = `我在freelog发现一个不错的节点：${title}`;
      window.open(
        `https://service.weibo.com/share/share.php?url=${encodeURIComponent(
          url,
        )}&title=${encodeURIComponent(weiboTitle)}&pic=${encodeURIComponent(cover || '')}`,
      );
    } else if (item.id === 'douban') {
      window.open(
        handleDoubanUrl({
          href: url,
          name: title,
          text: shareText,
          image: cover || '',
        }),
      );
    } else if (item.id === 'qq') {
      setPopupShow(false);
      setQrcodeShow(true);
      setQrcodeInfo({ name: item.name, url });
    } else if (item.id === 'wechat') {
      let wxQr = { name: item.name, url };
      if (nodeDomain !== '') {
        wxQr = {
          name: item.name,
          url: `https://share.testfreelog.com/?nodename=${nodeDomain}`,
        };
      }
      setPopupShow(false);
      setQrcodeShow(true);
      setQrcodeInfo(wxQr);
    }
  }

  function onCopyLink() {
    onClickShare?.();
    copy(url, { format: 'text/plain' });
    fMessage('链接复制成功～', 'success');
  }

  async function onDownloadQrCard() {
    const el = previewCardRef.current;
    if (!el) {
      fMessage('暂无法导出图片，请稍后重试', 'error');
      return;
    }
    try {
      const dataUrl = await toPng(el, {
        cacheBust: true,
        pixelRatio: 2,
      });
      const a = document.createElement('a');
      a.download = `${headline.replace(/[\\/:*?"<>|]/g, '_')}-名片.png`;
      a.href = dataUrl;
      a.click();
    } catch {
      fMessage('暂无法导出图片，请稍后重试', 'error');
    }
  }

  const coverSrc = resolveImgSrc(cover) || resolveImgSrc(defaultCover);

  return (
    <>
      <div role="presentation" onClick={() => setPopupShow(true)}>
        {children}
      </div>

      <Modal
        open={popupShow}
        onCancel={closeAll}
        footer={null}
        width={792}
        zIndex={1000}
        centered
        destroyOnClose={false}
        maskClosable
        className={styles.shareModal}
        closeIcon={<i className={`freelog fl-icon-guanbi ${styles.modalCloseIcon}`} />}
        title={<span className={styles.title}>分享</span>}
        styles={{
          content: { maxWidth: 'calc(100vw - 32px)' },
        }}
      >
        <div className={styles.body}>
          <div className={styles.leftCol}>
            <div ref={previewCardRef} className={styles.previewCard}>
              <div className={styles.previewTop}>
                <div className={styles.coverWrap}>
                  {coverSrc ? (
                    <img className={styles.coverImg} src={coverSrc} alt="" />
                  ) : null}
                </div>
                <div className={styles.cardTitle} title={headline}>
                  {headline}
                </div>
                <div
                  ref={cardDescRef}
                  className={styles.cardDesc}
                  title={introduction ? introduction : undefined}
                >
                  {cardDescShown}
                </div>
                <div className={styles.qrWrap}>
                  <QRCodeSVG value={url} size={134} />
                </div>
              </div>
              <div className={styles.footerRow}>
                <div className={styles.creator}>
                  {creatorAvatar ? (
                    <img className={styles.avatar} src={creatorAvatar} alt="" />
                  ) : (
                    <span className={styles.avatar} />
                  )}
                  <span className={styles.creatorName} title={creatorName}>
                    {creatorName || ' '}
                  </span>
                </div>
                <span className={styles.divider} />
                <FIcons.FFreelog className={styles.brand} aria-label="freelog" />
              </div>
            </div>

            <button type="button" className={styles.downloadBtn} onClick={onDownloadQrCard}>
              <FIcons.FDownload className={styles.downloadIcon} />
              下载二维码名片
            </button>
          </div>

          <div className={styles.rightCol}>
            <div className={styles.shareGrid}>
              <FTooltip title="复制链接">
                <div
                  role="button"
                  tabIndex={0}
                  className={styles.shareCell}
                  onClick={onCopyLink}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCopyLink();
                    }
                  }}
                >
                  <div className={`${styles.shareIconBox} ${styles.shareIconBoxOutlined}`}>
                    <FIcons.FCopy className={styles.shareIconDark} />
                  </div>
                  <span className={styles.shareLabel}>复制链接</span>
                </div>
              </FTooltip>

              {SHARE_ITEMS.map((item) => (
                <FTooltip title={`分享至${item.name}`} key={item.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.shareCell}
                    onClick={() => runShare(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        runShare(item);
                      }
                    }}
                  >
                    <div
                      className={`${styles.shareIconBox} ${styles.shareIconBoxFilled}`}
                      style={{ backgroundColor: item.bgColor }}
                    >
                      <i className={`freelog ${item.icon} ${styles.shareIcon}`} aria-hidden />
                    </div>
                    <span className={styles.shareLabel}>{item.name}</span>
                  </div>
                </FTooltip>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={qrcodeShow}
        onCancel={closeQr}
        footer={null}
        zIndex={1001}
        centered
        destroyOnClose
        width={360}
        className={styles.qrShareModal}
        title={`分享到${qrcodeInfo.name}`}
        closeIcon={<i className={`freelog fl-icon-guanbi ${styles.modalCloseIcon}`} />}
      >
        <div className={styles.qrModalBody}>
          {qrcodeInfo.url ? <QRCodeSVG value={qrcodeInfo.url} size={220} /> : null}
          <div className={styles.qrcodeText}>使用{qrcodeInfo.name}扫一扫完成分享</div>
        </div>
      </Modal>
    </>
  );
}
